import { useEffect, useMemo, useState } from 'react'
import { Calendar, Clock, Heart, Send, Star, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/AuthContext'
import {
  useCancelScheduledReminder,
  useSchedulePaymentThankYou,
  useScheduledReminders,
  useSendPaymentThankYou,
} from '@/hooks/useInvoices'
import { formatCurrency, formatDate, cn } from '@/lib/utils'
import type { Invoice, ScheduledReminder } from '@/types/invoice'

const GOOGLE_REVIEW_URL = 'https://g.page/r/CVNuwh4e6iPpEAE/review'

type Mode = 'now' | 'scheduled'

interface InvoicePaymentThankYouModalProps {
  invoice: Invoice | null
  isOpen: boolean
  onClose: () => void
}

function defaultScheduledAt(): string {
  // Maintenant + 1 heure, formaté pour <input type="datetime-local">
  const d = new Date(Date.now() + 60 * 60 * 1000)
  d.setSeconds(0, 0)
  const tzOffset = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16)
}

export function InvoicePaymentThankYouModal({
  invoice,
  isOpen,
  onClose,
}: InvoicePaymentThankYouModalProps) {
  const { user } = useAuth()
  const sendThankYou = useSendPaymentThankYou()
  const scheduleThankYou = useSchedulePaymentThankYou()
  const cancelReminder = useCancelScheduledReminder()
  const { data: thankYous } = useScheduledReminders(isOpen ? invoice?.id : undefined, 'thank_you')
  const pendingThankYous = useMemo(
    () => (thankYous || []).filter((r): r is ScheduledReminder => r.status === 'pending'),
    [thankYous]
  )

  const [mode, setMode] = useState<Mode>('now')
  const [scheduledAt, setScheduledAt] = useState<string>(defaultScheduledAt)

  useEffect(() => {
    if (isOpen) {
      setMode('now')
      setScheduledAt(defaultScheduledAt())
    }
  }, [isOpen])

  const scheduledDate = mode === 'scheduled' ? new Date(scheduledAt) : null
  const scheduledIsValid = scheduledDate && scheduledDate.getTime() > Date.now()

  const handleSubmit = async () => {
    if (!invoice) return

    if (mode === 'now') {
      if (!user?.id) {
        toast.error('Utilisateur non identifié')
        return
      }
      try {
        await sendThankYou.mutateAsync({ invoiceId: invoice.id, userId: user.id })
        toast.success('Email de remerciement envoyé')
        onClose()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Erreur lors de l'envoi")
      }
      return
    }

    if (!scheduledDate || !scheduledIsValid) {
      toast.error('La date de planification doit être dans le futur')
      return
    }
    if (!user?.id) {
      toast.error('Utilisateur non identifié')
      return
    }

    try {
      await scheduleThankYou.mutateAsync({
        invoiceId: invoice.id,
        scheduledAt: scheduledDate,
        userId: user.id,
      })
      toast.success(`Remerciement planifié pour le ${scheduledDate.toLocaleString('fr-FR')}`)
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de la planification')
    }
  }

  const handleCancelScheduled = async (id: string) => {
    try {
      await cancelReminder.mutateAsync(id)
      toast.success('Remerciement planifié annulé')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors de l'annulation")
    }
  }

  const isSubmitting = sendThankYou.isPending || scheduleThankYou.isPending
  const submitDisabled =
    !invoice?.client?.email ||
    isSubmitting ||
    (mode === 'scheduled' && !scheduledIsValid)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'now' ? 'Envoyer un remerciement' : 'Planifier un remerciement'}
    >
      <div className="space-y-4">
        {invoice && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('now')}
                className={cn(
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                  mode === 'now'
                    ? 'border-mint-500 bg-mint-50 text-mint-700'
                    : 'border-beige-200 bg-white text-brown-600 hover:bg-beige-50'
                )}
              >
                <Send className="w-4 h-4" />
                Maintenant
              </button>
              <button
                type="button"
                onClick={() => setMode('scheduled')}
                className={cn(
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                  mode === 'scheduled'
                    ? 'border-mint-500 bg-mint-50 text-mint-700'
                    : 'border-beige-200 bg-white text-brown-600 hover:bg-beige-50'
                )}
              >
                <Calendar className="w-4 h-4" />
                Planifier
              </button>
            </div>

            {pendingThankYous.length > 0 && (
              <div className="bg-mint-50 border border-mint-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-brown-800 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-mint-600" />
                  {pendingThankYous.length === 1
                    ? 'Un remerciement est déjà planifié'
                    : `${pendingThankYous.length} remerciements déjà planifiés`}
                </p>
                <ul className="space-y-1.5">
                  {pendingThankYous.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-2 bg-white rounded px-2.5 py-1.5 text-sm"
                    >
                      <span className="text-brown-700">
                        {new Date(r.scheduled_at).toLocaleString('fr-FR', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCancelScheduled(r.id)}
                        disabled={cancelReminder.isPending}
                        className="text-brown-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Annuler ce remerciement"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-brown-600">
              {mode === 'now' ? 'Envoyer un email de remerciement' : "Planifier l'envoi d'un remerciement"} pour la facture{' '}
              <span className="font-mono font-medium text-brown-900">{invoice.numero}</span>
              {' ?'}
            </p>

            <div className="bg-beige-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-brown-500">Destinataire</span>
                <span className="font-medium text-brown-900 truncate">
                  {invoice.client?.email || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-brown-500">Montant réglé</span>
                <span className="font-medium text-brown-900">
                  {formatCurrency(invoice.total_ttc || 0)}
                </span>
              </div>
              {invoice.date_paiement && (
                <div className="flex justify-between gap-4">
                  <span className="text-brown-500">Date de paiement</span>
                  <span className="font-medium text-mint-600">
                    {formatDate(invoice.date_paiement)}
                  </span>
                </div>
              )}
            </div>

            {mode === 'scheduled' && (
              <div className="space-y-2">
                <label htmlFor="thankyou-scheduled-at" className="text-sm font-medium text-brown-700">
                  Date et heure d'envoi
                </label>
                <input
                  id="thankyou-scheduled-at"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={defaultScheduledAt().slice(0, 16)}
                  className="w-full px-3 py-2 rounded-lg border border-beige-200 bg-white text-brown-900 focus:outline-none focus:ring-2 focus:ring-mint-500"
                />
                {!scheduledIsValid && (
                  <p className="text-xs text-red-600">La date doit être dans le futur.</p>
                )}
                <p className="text-xs text-brown-500">
                  L'envoi sera traité à cette heure (précision ~1 minute). Si la facture
                  redevient impayée d'ici là, l'envoi est automatiquement annulé.
                </p>
              </div>
            )}

            <div className="bg-mint-50 border-l-4 border-mint-500 rounded p-3 text-sm text-brown-700 flex gap-2">
              <Star className="w-4 h-4 text-mint-600 flex-shrink-0 mt-0.5" />
              <span>
                L'email contiendra un bouton pour laisser un avis sur Google :{' '}
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-mint-700 hover:text-mint-800"
                >
                  voir la page d'avis
                </a>
                .
              </span>
            </div>

            {!invoice.client?.email && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded p-3 text-sm text-red-700">
                Ce client n'a pas d'adresse email — l'envoi va échouer.
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 pt-2 justify-end">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={submitDisabled}>
            {mode === 'now' ? (
              <>
                <Heart className="w-4 h-4 mr-1" />
                Envoyer le remerciement
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-1" />
                Planifier le remerciement
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
